/**
 * MSAL Authentication Configuration
 * AIDLC Administrative Backend
 *
 * Authorization Code Flow with PKCE (per Bill's security review requirements).
 * Token storage: sessionStorage (not localStorage) per security checklist item 2.
 * Scopes: minimal per security checklist item 5.
 *
 * IMPORTANT: Client ID and Tenant ID must come from environment variables.
 * Never hardcode secrets. See Todd's Infrastructure Guide for Key Vault setup.
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

const MSAL_CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID || '';
const MSAL_TENANT_ID = import.meta.env.VITE_MSAL_TENANT_ID || '';
const MSAL_REDIRECT_URI = import.meta.env.VITE_MSAL_REDIRECT_URI || window.location.origin;

if (!MSAL_CLIENT_ID) {
  console.error('[MSAL] VITE_MSAL_CLIENT_ID is not configured. Authentication will fail.');
}

if (!MSAL_TENANT_ID) {
  console.error('[MSAL] VITE_MSAL_TENANT_ID is not configured. Authentication will fail.');
}

export const msalConfig: Configuration = {
  auth: {
    clientId: MSAL_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MSAL_TENANT_ID}`,
    redirectUri: MSAL_REDIRECT_URI,
    postLogoutRedirectUri: MSAL_REDIRECT_URI,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          default:
            break;
        }
      },
    },
  },
};

/**
 * Scopes for login -- minimal per Bill's security requirements
 */
export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/**
 * Scopes for API access -- custom scope from Entra ID app registration
 */
export const apiRequest = {
  scopes: [`api://${MSAL_CLIENT_ID}/access_as_user`],
};

/** RBAC role constants: kept in utils/roles.ts with the on-screen naming rule, re-exported here as before. */
export { AppRoles, type AppRole } from './utils/roles';
